import { DOCUMENT } from '@angular/common'
import { Service, inject } from '@angular/core'
import { PopupService } from '../popup-service/popup.service'

/** Scrolls the front screen back to the top when the user taps the iOS status bar, like native apps. */
@Service()
export class ScrollToTopService {
  private readonly document = inject(DOCUMENT)
  private readonly popups = inject(PopupService)

  /**
   * Starts listening to status bar taps. The Capacitor status bar plugin sends `statusTap` to the window.
   * @returns {() => void} Stops listening.
   */
  listen(): () => void {
    const view = this.document.defaultView
    const onStatusTap = () => this.scrollToTop()

    view?.addEventListener('statusTap', onStatusTap)

    return () => view?.removeEventListener('statusTap', onStatusTap)
  }

  /** Scrolls the area under the middle of the screen to the top. */
  private scrollToTop(): void {
    const view = this.document.defaultView

    if (!view) {
      return
    }

    const isReducedMotion = view.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

    this.findScrolledArea(view)?.scrollTo({ top: 0, behavior: isReducedMotion ? 'auto' : 'smooth' })
  }

  /**
   * Finds the nearest scrolled area around the middle of the screen. An open sheet limits the search, so a tap
   * never scrolls the screens it covers.
   * @param {Window} view Window of the app.
   * @returns {Element | null} The area to scroll, or nothing when the front screen is already at the top.
   */
  private findScrolledArea(view: Window): Element | null {
    const frontSheet = this.popups.topElement()
    let element = this.document.elementFromPoint(view.innerWidth / 2, view.innerHeight / 2)

    while (element && (!frontSheet || frontSheet.contains(element))) {
      if (element.scrollTop > 0 && this.scrollsVertically(element, view)) {
        return element
      }

      element = element.parentElement
    }

    const page = this.document.scrollingElement

    return !frontSheet && page && page.scrollTop > 0 ? page : null
  }

  /**
   * Tests whether the user can scroll an element up and down. Hidden overflow can be scrolled only by code.
   * @param {Element} element Element to test.
   * @param {Window} view Window of the app.
   * @returns {boolean} Whether the element is a vertical scroll area.
   */
  private scrollsVertically(element: Element, view: Window): boolean {
    const { overflowY } = view.getComputedStyle(element)

    return overflowY === 'auto' || overflowY === 'scroll'
  }
}
